import { useState, useEffect, useMemo, useCallback } from "react";
import { DistinctValue } from "../../../../types";
import {
  reclassifyValues as apiReclassifyValues,
  TableInfo,
  EntityFieldItem,
} from "../../../../services/cleaning-data-service";
import { useDialogs } from "../../../../../dialog/use-dialog";
import { useNotification } from "../../../../../../context/notification-provider";
import useAllTables from "../../../../hooks/use-all-tables";
import useTableFields from "../../../../hooks/use-table-fields";

interface UseReclassifyValuesParams {
  fieldId: number | undefined;
  checked: { [key: string]: boolean };
  mainValues: DistinctValue[];
  selectedItem: DistinctValue | null;
  refreshDistinctValues: () => void;
}

interface UseReclassifyValuesReturn {
  targetTable: TableInfo | null;
  targetField: EntityFieldItem | null;
  availableTables: TableInfo[] | undefined;
  fieldsForEntity: EntityFieldItem[] | undefined;
  valuesToReclassify: DistinctValue[];
  message: string;
  setTargetTable: (table: TableInfo | null) => void;
  setTargetField: (field: EntityFieldItem | null) => void;
  handleReclassify: () => Promise<void>;
  handleCancel: () => void;
}

export default function useReclassifyValues({
  fieldId,
  checked,
  mainValues,
  selectedItem,
  refreshDistinctValues,
}: UseReclassifyValuesParams): UseReclassifyValuesReturn {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();

  const [targetTable, setTargetTable] = useState<TableInfo | null>(null);
  const [targetField, setTargetField] = useState<EntityFieldItem | null>(null);

  // Fetch all available tables/entities
  const { availableTables } = useAllTables();

  // Fetch fields for selected entity
  const { fields: fieldsForEntity } = useTableFields(targetTable?.tableId);

  // Reset field when entity changes
  useEffect(() => {
    setTargetField(null);
  }, [targetTable]);

  // Get values to reclassify: prioritize checked items, then fall back to selected item
  const valuesToReclassify = useMemo(() => {
    const checkedValues = mainValues.filter((val) => checked[val.id]);
    if (checkedValues.length > 0) {
      return checkedValues;
    }
    if (selectedItem) {
      return [selectedItem];
    }
    return [];
  }, [checked, mainValues, selectedItem]);

  // Determine the message to display
  const message = useMemo(() => {
    const checkedCount = Object.values(checked).filter(Boolean).length;
    if (checkedCount > 0) {
      return `Reclassify ${checkedCount} selected value(s) to a different entity/field:`;
    }
    if (selectedItem) {
      return `Reclassify "${selectedItem.value}" to a different entity/field:`;
    }
    return "Select values to reclassify to a different entity/field:";
  }, [checked, selectedItem]);

  const handleReclassify = useCallback(async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    if (!targetTable) {
      showNotification({
        message: "Please select a target entity.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    if (!targetField) {
      showNotification({
        message: "Please select a target field.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    if (valuesToReclassify.length === 0) {
      showNotification({
        message: "No values selected to reclassify.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    // Get values to reclassify as simple strings
    const valuesToReclassifyStrings = valuesToReclassify.map(
      (val) => val.value,
    );

    try {
      const result = await confirm(
        `Are you sure you want to reclassify ${valuesToReclassify.length} value${valuesToReclassify.length > 1 ? "s" : ""} to entity "${targetTable.tableName}" / field "${targetField.name}"?`,
        {
          title: "Confirm Reclassify Values",
          okText: "Reclassify",
          cancelText: "Cancel",
          severity: "info",
        },
      );

      if (result === true) {
        try {
          await apiReclassifyValues(
            fieldId,
            targetField.id,
            valuesToReclassifyStrings,
          );
          refreshDistinctValues();
          showNotification({
            message: `${valuesToReclassify.length} value${valuesToReclassify.length > 1 ? "s" : ""} reclassified successfully.`,
            severity: "success",
            duration: 3000,
          });
          // Reset state
          setTargetTable(null);
          setTargetField(null);
        } catch (err) {
          showNotification({
            message: `Failed to reclassify values. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch (err) {
      showNotification({
        message: `Failed to show confirmation dialog. ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  }, [
    fieldId,
    targetTable,
    targetField,
    valuesToReclassify,
    showNotification,
    confirm,
    refreshDistinctValues,
  ]);

  const handleCancel = useCallback(() => {
    setTargetTable(null);
    setTargetField(null);
  }, []);

  return {
    targetTable,
    targetField,
    availableTables,
    fieldsForEntity,
    valuesToReclassify,
    message,
    setTargetTable,
    setTargetField,
    handleReclassify,
    handleCancel,
  };
}
